import * as React from 'react';
import Axios from 'axios';
import type { JsonObject } from '@/app-package';
import { AnsweringOrgGroupDoc, OrgDoc } from '@bb/domain/dist/schemas';

import errorReporter from '@app/errorReporter';
import BrokerageGroupingSelect from '../BrokerageGroupingSelect';
import BrokerageLogo from '../../../react-common/BrokerageLogo';
import Loading from '../../../react-common/Loading';
import PanelBody from '../../../react-common/PanelBody';
import PanelHeader from '../../../react-common/PanelHeader';
import BrokerageSettingsAddModal from '../BrokerageSettingsAddModal';

type Org = JsonObject<OrgDoc>;
type AnsweringOrgGroup = JsonObject<AnsweringOrgGroupDoc>;

interface BrokerageGroupingState {
  groups: (Omit<AnsweringOrgGroup, 'orgs'> & { orgs: Org[] })[];
  loading: boolean;
  orgs: OrgDoc[];
  manageGroupModalVisible: boolean;
  fetching: boolean;
  pendingGroupChanges: string[];
}

class BrokerageGrouping extends React.Component<{}, BrokerageGroupingState> {
  constructor(props: {}) {
    super(props);
    this.state = {
      groups: [],
      orgs: [],
      loading: true,
      manageGroupModalVisible: false,
      fetching: false,
      pendingGroupChanges: [],
    };
  }

  public async componentDidMount() {
    await this.fetchData();
  }

  public fetchData = async (
    { brokerages, groups }: { brokerages?: boolean; groups?: boolean } = {
      brokerages: true,
      groups: true,
    },
  ) => {
    try {
      this.setState({ loading: true });
      const fetchPromises = [];
      if (brokerages) fetchPromises.push(this.fetchBrokerages());
      if (groups) fetchPromises.push(this.fetchGroups());
      if (fetchPromises.length > 0) await Promise.all(fetchPromises);
    } catch (err) {
      errorReporter.error(err);
    } finally {
      this.setState({ loading: false });
    }
  };

  public fetchBrokerages = async () => {
    try {
      this.setState({ fetching: true });
      const {
        data: { brokerages },
      } = await Axios.get<{ brokerages: OrgDoc[] }>('/answering/brokerages');
      this.setState({ orgs: brokerages });
    } catch (err) {
      errorReporter.error(`Error fetching brokerages: ${err}`);
    } finally {
      this.setState({ fetching: false });
    }
  };

  public fetchGroups = async (setFetching = true) => {
    try {
      if (setFetching) this.setState({ fetching: true });
      const { data } =
        await Axios.get<(Omit<AnsweringOrgGroup, 'orgs'> & { orgs: Org[] })[]>(
          '/answering/groups',
        );
      this.setState({ groups: data });
    } catch (err) {
      errorReporter.error(`Error fetching groups: ${err}`);
    } finally {
      if (setFetching) this.setState({ fetching: false });
    }
  };

  public handleSelectChange = async ({
    id,
    orgId,
  }: {
    id: string;
    orgId: string;
  }) => {
    try {
      let { pendingGroupChanges } = this.state;
      pendingGroupChanges = [...new Set([...pendingGroupChanges, orgId])];
      this.setState({ pendingGroupChanges });
      await Axios.post('/answering/add_org_to_group', { groupId: id, orgId });
    } catch (err) {
      errorReporter.error(err);
    } finally {
      const { pendingGroupChanges } = this.state;
      pendingGroupChanges.splice(pendingGroupChanges.indexOf(orgId), 1);
      this.setState({ pendingGroupChanges });
    }
  };

  public closeManageGroupModal = async () => {
    try {
      this.setState({ manageGroupModalVisible: false });
    } catch (err) {
      errorReporter.error(err);
    }
  };

  public openManageGroupModal = async () => {
    try {
      this.setState({ manageGroupModalVisible: true });
      await this.fetchGroups();
    } catch (err) {
      errorReporter.error(err);
    }
  };

  public render() {
    const {
      groups,
      fetching,
      loading,
      orgs,
      manageGroupModalVisible,
      pendingGroupChanges,
    } = this.state;

    return (
      <>
        <BrokerageSettingsAddModal
          closeModal={this.closeManageGroupModal}
          fetchGroups={this.fetchGroups}
          fetching={fetching}
          groups={groups}
          visible={manageGroupModalVisible}
        />
        <PanelHeader
          title="Brokerage Grouping"
          extra={[
            <button
              className="btn btn-primary"
              onClick={this.openManageGroupModal}
              key="1"
            >
              Manage Groups
            </button>,
          ]}
        />
        <PanelBody>
          {loading ? (
            <Loading size="fa-5x" />
          ) : (
            <table className="table table-bordered table-striped brokerage-grouping-table">
              <colgroup>
                <col className="col-lg-2 col-xs-3" />
                <col className="col-lg-8 col-xs-6" />
                <col className="col-lg-2 col-xs-3" />
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  <th>Brokerage</th>
                  <th>Group</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map(org => (
                  <tr className="brokerage-grouping-row" key={org.id}>
                    <td
                      className="brokerage-logo-cell"
                      style={{ verticalAlign: 'middle', textAlign: 'center' }}
                    >
                      <BrokerageLogo logoUrl={org.logoUrl} />
                    </td>
                    <td>
                      <div style={{ fontSize: '1.5em', fontWeight: 'bold' }}>
                        {org.mls_name}
                      </div>
                      <div style={{ fontSize: '0.95em', fontWeight: 'bold' }}>
                        {org.addresses &&
                          org.addresses[0] &&
                          org.addresses[0][0] &&
                          org.addresses[0][0].address}
                      </div>
                    </td>
                    <td>
                      <BrokerageGroupingSelect
                        handleChange={this.handleSelectChange}
                        pending={pendingGroupChanges.length > 0}
                        groups={groups}
                        orgId={org.id}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </PanelBody>
      </>
    );
  }
}

export default BrokerageGrouping;
